import { Api } from '../../utils/ApiService';
import { getDecodedToken } from '../../utils/auth';

export class PermissionsService {
  private fetchPromise?: Promise<string[]>;

  private loadedForKey = '';

  isLoadedForCurrentToken(): boolean {
    return this.loadedForKey === this.cacheKey();
  }

  async load(api: Api, endpointPermissions: string): Promise<string[]> {
    const cacheKey = this.cacheKey();

    if (!this.fetchPromise) {
      this.fetchPromise = this.fetchPermissionsFromApi(
        api,
        endpointPermissions,
        cacheKey
      );
    }

    try {
      return await this.fetchPromise;
    } finally {
      this.fetchPromise = undefined;
    }
  }

  private async fetchPermissionsFromApi(
    api: Api,
    endpointPermissions: string,
    cacheKey: string
  ): Promise<string[]> {
    const response = await api.fetch({
      endpoint: `${endpointPermissions}/me`,
      method: 'GET'
    });
    const payload = response as { permissions?: unknown };

    if (
      !response ||
      typeof response !== 'object' ||
      !Array.isArray(payload.permissions)
    ) {
      this.loadedForKey = cacheKey;
      return [];
    }

    const permissions = payload.permissions
      .map((permission) => this.normalizePermission(permission))
      .filter(Boolean);
    this.loadedForKey = cacheKey;
    return permissions;
  }

  private normalizePermission(permission: unknown): string {
    return String(permission || '')
      .trim()
      .toUpperCase();
  }

  private cacheKey(): string {
    const decoded = getDecodedToken();
    const subject =
      typeof decoded?.sub === 'string' ? decoded.sub : 'anonymous';
    const expiry =
      typeof decoded?.exp === 'number' ? String(decoded.exp) : 'session';

    return `mesh.permissions.${subject}.${expiry}`;
  }
}
